
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { capacitorStorage } from "@/lib/capacitorStorage";
import { useUser } from "@/contexts/UserContext";
import LanguageSelector from "@/components/LanguageSelector";
import LegalModal from "@/components/LegalModal";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import bgCouple from '@/res/bg-couple.jpg';
import logoImg from "../res/betterhusband_black.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Module-level guard: prevents both deep-link AND browserFinished from
// finalizing OAuth at the same time (race condition).
let _oauthHandled = false;

// Module-level guard: prevents finalizeOAuth from running twice on re-renders
let _finalizeOAuthRunning = false;

// Cached PKCE code_verifier — read from Capacitor Preferences right after
// signInWithOAuth stores it, so we can fall back to a direct HTTP exchange
// if the Supabase JS SDK hangs on Capacitor storage during exchangeCodeForSession.
let _cachedCodeVerifier = null;

// Reset all module-level guards together to prevent stale locks on failure/timeout.
function resetAllGuards() {
  _oauthHandled = false;
  _finalizeOAuthRunning = false;
  _cachedCodeVerifier = null;
}

/* =========================================================================================
   Utilities comuni
   ========================================================================================= */

// Attende che la sessione Supabase sia pronta per evitare race subito dopo il callback OAuth

async function waitForSession(maxMs = 4000) {
  // 1) Prova subito
  const initial = await supabase.auth.getSession();
  if (initial?.data?.session?.user?.id) return initial.data.session;

  // 2) Ascolta cambi di stato
  let resolver;
  const sessionPromise = new Promise((resolve) => (resolver = resolve));

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user?.id) resolver(session);
  });

  // 3) Timeout di sicurezza
  const timeoutPromise = new Promise((r) => setTimeout(() => r(null), Math.max(800, maxMs)));

  const result = await Promise.race([sessionPromise, timeoutPromise]);
  // Always clean up the listener
  try { subscription.unsubscribe(); } catch (_) {}
  return result;
}

// Parse tokens from URL query or hash string
function parseTokensFromUrl(url) {
  try {
    const urlObj = new URL(url);
    const query = urlObj.searchParams;
    let accessToken = query.get('access_token');
    let refreshToken = query.get('refresh_token');

    // hash-based tokens could be appended by OAuth provider
    const hash = urlObj.hash ? new URLSearchParams(urlObj.hash.replace('#', '')) : null;
    if (!accessToken && hash) accessToken = hash.get('access_token');
    if (!refreshToken && hash) refreshToken = hash.get('refresh_token');

    return { accessToken, refreshToken };
  } catch (err) {
    console.warn('parseTokensFromUrl failed, fallback manual parsing', err);
    const q = url.includes('?') ? new URLSearchParams(url.split('?')[1]) : new URLSearchParams();
    const hash = url.includes('#') ? new URLSearchParams(url.split('#')[1]) : new URLSearchParams();
    return {
      accessToken: q.get('access_token') || hash.get('access_token'),
      refreshToken: q.get('refresh_token') || hash.get('refresh_token'),
    };
  }
}


// Nuova utility

// ✅ Usa solo /auth/login/google, con upsert lato backend
async function upsertBackendUserFromGoogle({ session, API }) {
  const user = session.user;
  const accessToken = session.access_token;

  const email = user.email ||
                user.user_metadata?.email ||
                user.identities?.[0]?.identity_data?.email;

  const name = user.user_metadata?.full_name ||
               user.user_metadata?.name ||
               user.identities?.[0]?.identity_data?.full_name ||
               (email ? email.split("@")[0] : "Utente");

  const picture = user.user_metadata?.avatar_url ||
                  user.identities?.[0]?.identity_data?.avatar_url;

  // Update picture in Supabase directly — non-blocking, may fail on Capacitor
  // when setSession didn't persist (capacitorStorage deadlock).
  try {
    await Promise.race([
      supabase.from('users').update({ picture: picture || null }).eq('user_id', user.id),
      new Promise((_, reject) => setTimeout(() => reject(new Error('pic update timeout')), 4000)),
    ]);
  } catch (_) { /* non-critical — backend upsert handles it */ }

  // Unica chiamata: il backend deve fare upsert (create se non esiste)
  const loginUrl = `${API}/auth/login/google`;
  console.log('[upsertBackend] Calling:', loginUrl, '| user_id:', user.id, '| email:', email);
  
  const res = await axios.post(
    loginUrl,
    { id: user.id, email, name, picture }, // payload utile per upsert
    { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } }
  );

  console.log('[upsertBackend] Response:', res.status, res.data?.success, '| needs_onboarding:', res.data?.needs_onboarding);

  return res.data; // { language, needs_onboarding, ... }
}



// Scarica il profilo completo dell’utente autenticato
async function fetchUserProfile({ accessToken, useCookie = false, API }) {
  let axiosCfg = {};
  
  if (useCookie) {
    // For cookie-based auth, use withCredentials
    axiosCfg = { withCredentials: true };
  } else if (accessToken) {
    // For token-based auth, use Authorization header
    axiosCfg = { headers: { Authorization: `Bearer ${accessToken}` } };
  }
  // If neither useCookie nor accessToken, rely on axios defaults that were set during login
    console.log('fetchUserProfile config:', axiosCfg);
  console.log('Axios defaults headers:', axios.defaults.headers.common);
    const { data } = await axios.get(`${API}/users/me`, { ...axiosCfg, timeout: 10000 });
  // Return just the profile object, not the full response
  return data?.profile || data; // profilo completo (preferences, roles, settings, ecc.)
}

// Applica lingua, salva id, scarica profilo (se non serve onboarding) e naviga
async function finalizeAndRoute({
  userId,
  accessToken,
  language,
  needs_onboarding,
  subscription_status = 'free',
  i18n,
  navigate,
  onLogin,        // callback opzionale legacy
  useCookie = false,
  updateUser,     // opzionale: aggiorna UserContext con profilo completo
  fetchUserData,  // opzionale: ricarica profilo dal server
  fromOnboarding = false,
}) {

  // lingua — always preserve the current language selection (important for OAuth
  // flows where the backend may return null for new users)
  // Client-side localStorage choice takes priority over backend-returned language
  // because the user may have explicitly picked a language during onboarding BEFORE auth.
  const savedLang = localStorage.getItem('betterhusbandUserLang');
  language = savedLang || language || i18n.language || 'en';
  if (language !== i18n.language) {
    await i18n.changeLanguage(language);
  }
  localStorage.setItem("betterhusbandUserLang", language);

  // If we came from onboarding pre-auth, clear the marker.
  if (fromOnboarding) {
    localStorage.removeItem('bh_oauth_from_onboarding');
  }

  // salva id
  if (userId) localStorage.setItem('betterhusbandUserId', userId);
  if (accessToken) localStorage.setItem('bh_access_token', accessToken);

  // se serve onboarding → check if we have locally saved onboarding data (pre-auth flow)
  if (needs_onboarding) {
    // Same guard as the backend: a payload with no real answers (only schema
    // defaults) must NOT be replayed, otherwise the user ends up flagged as
    // onboarded with an empty profile.
    const payloadHasRealAnswers = (p) => {
      if (!p || typeof p !== 'object') return false;
      const scalars = ['relationship_type','partner_name','age_cluster','work_schedule','referral_source','pain_autopilot','pain_energy','pain_gestures','pain_connection'];
      if (scalars.some(k => p[k] && p[k] !== '' && p[k] !== 'none')) return true;
      const lists = ['motivation','improvement_areas','tension_points'];
      if (lists.some(k => Array.isArray(p[k]) && p[k].length > 0)) return true;
      return false;
    };

    let parsedPayload = null;
    const localOnboarding = localStorage.getItem('bh_onboarding_data');
    if (localOnboarding) {
      try { parsedPayload = JSON.parse(localOnboarding); } catch (_) { parsedPayload = null; }
    }

    if (parsedPayload && payloadHasRealAnswers(parsedPayload)) {
      // User completed onboarding before auth — submit data now
      try {
        const headers = accessToken
          ? { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
          : { 'Content-Type': 'application/json' };
        await axios.post(`${API}/profile/onboarding-v2`, parsedPayload, { headers, timeout: 10000 });
        localStorage.removeItem('bh_onboarding_data');
        localStorage.removeItem('bh_onboarding_form');

        // Check if user is beta tester — skip paywall, go to dashboard
        try {
          const betaRes = await axios.get(`${API}/auth/check-beta`, { headers, timeout: 8000 });
          if (betaRes.data?.is_beta) {
            onLogin?.(userId);
            navigate("/dashboard", { replace: true });
            return;
          }
        } catch (betaErr) {
        }
      } catch (err) {
      }
      onLogin?.(userId);
      navigate("/onboarding?step=paywall", { replace: true });
      return;
    }

    // No usable onboarding data cached — clear any stale partial state and
    // send the user through the full questionnaire so we capture real answers
    // before account/profile finalization. We never drop them at the paywall
    // without data: that's how ghost-onboarded users were being created.
    try {
      localStorage.removeItem('bh_onboarding_data');
      localStorage.removeItem('bh_onboarding_form');
    } catch (_) {}

    onLogin?.(userId);
    navigate("/onboarding", { replace: true });
    return;
  }

  // onboarding completed but payment not fulfilled → go to paywall (unless beta tester)
  if (subscription_status !== 'premium') {
    // Check if beta tester — skip paywall
    try {
      const headers = accessToken
        ? { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' };
      const betaRes = await axios.get(`${API}/auth/check-beta`, { headers, timeout: 8000 });
      if (betaRes.data?.is_beta) {
        onLogin?.(userId);
        navigate("/dashboard", { replace: true });
        return;
      }
    } catch (betaErr) {
    }
    onLogin?.(userId);
    navigate("/onboarding?step=paywall", { replace: true });
    return;
  }

  // altrimenti carica profilo completo prima di entrare in dashboard
  try {
    const fullProfile = await fetchUserProfile({ accessToken, useCookie, API });
    localStorage.setItem("betterhusbandUser", JSON.stringify(fullProfile));
    updateUser?.(fullProfile);
    onLogin?.(userId, fullProfile);
    navigate("/dashboard", { replace: true });
  } catch (e) {
    onLogin?.(userId);
    navigate("/dashboard", { replace: true });
  }
}

/* =========================================================================================
   Hook: finalizza login Google al ritorno su /login?oauth=success
   ========================================================================================= */


function useGoogleAuthFinalize(onLogin, setGoogleLoading, updateUser, fetchUserData, setMobileAppLink) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Check if user is already logged in (for Capacitor return from external OAuth)
  const checkExistingSession = React.useCallback(async () => {
    // If user explicitly logged out, don't auto-login from a stale session
    const explicitLogout = localStorage.getItem('betterhusbandExplicitLogout');
    if (explicitLogout === '1') {
      return;
    }

    // If Supabase put tokens in the URL hash, parse them into a session first
    if (window.location.hash && window.location.hash.includes('access_token')) {
      const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
      const hashAccess = hashParams.get('access_token');
      const hashRefresh = hashParams.get('refresh_token');
      if (hashAccess && hashRefresh) {
        try {
          await supabase.auth.setSession({ access_token: hashAccess, refresh_token: hashRefresh });
          console.log('[checkExistingSession] Session set from URL hash tokens');
        } catch (err) {
          console.warn('[checkExistingSession] Failed to set session from hash tokens', err);
        }
      }
    }

    let mounted = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id && mounted) {
        console.log('Found existing session, finalizing login...');
        setGoogleLoading?.(true);

        // If we are running inside a mobile browser (e.g. Chrome Custom Tab from
        // OAuth flow), redirect to the native app via deep link instead of
        // navigating within the web page.
        const isMobileBrowser = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isNotCapacitor = !Capacitor.isNativePlatform();
        if (isMobileBrowser && isNotCapacitor) {
          const params = `access_token=${encodeURIComponent(session.access_token)}&refresh_token=${encodeURIComponent(session.refresh_token)}`;
          const isAndroid = /Android/i.test(navigator.userAgent);

          // Android requires intent:// URIs — Chrome blocks plain custom schemes
          const deepLink = isAndroid
            ? `intent://login?${params}#Intent;scheme=betterhusband;package=com.greathusband.app;end`
            : `betterhusband://login?${params}`;

          console.log('[checkExistingSession] Mobile browser detected, redirecting to deep link');
          if (setMobileAppLink) setMobileAppLink(deepLink);
          window.location.replace(deepLink);
          setGoogleLoading?.(false);
          return;
        }

        // User already has a session, finalize login
        try {
          const { language, needs_onboarding, subscription_status } = await Promise.race([
            upsertBackendUserFromGoogle({ session, API }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Backend timeout')), 12000)),
          ]);
          const fromOnboarding = new URLSearchParams(window.location.search).get('from') === 'onboarding' || localStorage.getItem('bh_oauth_from_onboarding') === '1';
          await finalizeAndRoute({
            userId: session.user.id,
            accessToken: session.access_token,
            language,
            needs_onboarding,
            subscription_status,
            i18n,
            navigate,
            onLogin,
            useCookie: false,
            updateUser,
            fetchUserData,
            fromOnboarding,
          });
        } catch (innerErr) {
          console.error('[checkExistingSession] Finalize failed:', innerErr);
          setGoogleLoading?.(false);
        }
      }
    } catch (err) {
      console.error('Error checking existing session:', err);
    } finally {
      if (mounted) setGoogleLoading?.(false);
    }
  }, [setGoogleLoading, setMobileAppLink, i18n, navigate, onLogin, updateUser, fetchUserData]);

  useEffect(() => {
    // Check if coming from OAuth callback (oauth=success, code, or hash tokens)
    const url = new URL(window.location.href);
    const hasOAuth = url.searchParams.get("oauth") === "success";
    const hasCode = !!url.searchParams.get("code");
    const isFromOnboarding = url.searchParams.get("from") === "onboarding";
    // Don't auto-login from a stale session when arriving from onboarding —
    // the user needs to actively choose Google or email to create/link their account.
    if (!hasOAuth && !hasCode && !isFromOnboarding) {
      checkExistingSession();
    }
    return () => { };
  }, [checkExistingSession]);

  const finalizeOAuth = React.useCallback(async () => {
    const url = new URL(window.location.href);
    const fromOAuth = url.searchParams.get("oauth") === "success";
    const pkceCode = url.searchParams.get("code");
    // Run if we have oauth=success OR a PKCE code
    if (!fromOAuth && !pkceCode) return;

    // Compute fromOnboarding inside the callback to avoid dependency instability
    const fromOnboarding = new URLSearchParams(window.location.search).get('from') === 'onboarding' || localStorage.getItem('bh_oauth_from_onboarding') === '1';

    // User is actively coming back from OAuth, clear explicit logout flag
    localStorage.removeItem('betterhusbandExplicitLogout');

    // Module-level guard prevents double execution from re-renders
    if (_finalizeOAuthRunning) return;
    _finalizeOAuthRunning = true;

    setGoogleLoading?.(true);

    try {
      // ── DO NOT manually exchange the PKCE code ──
      // detectSessionInUrl: true handles the exchange automatically.
      // Calling exchangeCodeForSession with an already-consumed code
      // invalidates the session. Just wait for the session below.

      // Clean the code from the URL to prevent confusing re-runs
      if (pkceCode) {
        console.log('[finalizeOAuth] PKCE code in URL — detectSessionInUrl will handle exchange');
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        if (!cleanUrl.searchParams.has('oauth')) cleanUrl.searchParams.set('oauth', 'success');
        window.history.replaceState({}, '', cleanUrl.toString());
      }

      // If we got tokens directly in URL (deep link / native), apply them
      const { accessToken: urlAccess, refreshToken: urlRefresh } = parseTokensFromUrl(window.location.href);
      if (urlAccess || urlRefresh) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: urlAccess,
            refresh_token: urlRefresh,
          });
          if (error) {
            console.warn('Could not apply tokens from URL to local supabase session', error);
          } else if (data?.session) {
            console.log('Supabase session established from URL tokens');
          }
        } catch (err) {
          console.warn('Error while setting session from URL tokens', err);
        }
      }

      // Wait for session — detectSessionInUrl fires onAuthStateChange(SIGNED_IN)
      let session = null;
      const immediate = await supabase.auth.getSession();
      if (immediate?.data?.session?.user?.id) {
        session = immediate.data.session;
      } else {
        // Session may not be set yet — wait for auth state change
        session = await waitForSession(10000);
      }
      if (!session?.user?.id) {
        console.error("Sessione Supabase non disponibile:", session);
        toast.error(t("auth.loginFailed") || "Login fallito");
        navigate("/", { replace: true });
        return;
      }

      // Check if this is a mobile browser (the OAuth callback landed on the web
      // page inside Chrome Custom Tab / Safari). We must redirect back to the
      // native app via deep link so the in-app browser closes.
      const isMobileBrowser = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isNotCapacitor = !Capacitor.isNativePlatform();
      const isNativeReturn = new URL(window.location.href).searchParams.get('native') === '1';

      if ((isMobileBrowser && isNotCapacitor) || isNativeReturn) {
        // Build deep link with tokens to hand off to the native app
        const fromOnb = localStorage.getItem('bh_oauth_from_onboarding') === '1' ? '&from_onboarding=1' : '';
        const params = `access_token=${encodeURIComponent(session.access_token)}&refresh_token=${encodeURIComponent(session.refresh_token)}${fromOnb}`;
        const isAndroid = /Android/i.test(navigator.userAgent);

        // Android: Chrome blocks JS navigation to custom schemes.
        // Use intent:// URIs which Chrome Custom Tabs handle correctly.
        const deepLink = isAndroid
          ? `intent://login?${params}#Intent;scheme=betterhusband;package=com.greathusband.app;end`
          : `betterhusband://login?${params}`;

        console.log('[OAuth web callback] Redirecting to native deep link');
        if (setMobileAppLink) setMobileAppLink(deepLink);
        window.location.replace(deepLink);
        setGoogleLoading?.(false);
        // Don't navigate in React — let the deep link take the user back to the app
        return;
      }

      // 2) salva nome e foto google (per onboarding)
      const user = session.user;
      const googleName =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.identities?.[0]?.identity_data?.full_name;
      if (googleName) localStorage.setItem("betterhusbandUserName", googleName);

      const googlePicture =
        user.user_metadata?.avatar_url ||
        user.identities?.[0]?.identity_data?.avatar_url;
      if (googlePicture) localStorage.setItem("betterhusbandGooglePicture", googlePicture);

      // 3) upsert lato backend: se non esiste → crealo
      const { language, needs_onboarding, subscription_status } =
        await upsertBackendUserFromGoogle({ session, API });

      // 4) rotta finale
      await finalizeAndRoute({
        userId: user.id,
        accessToken: session.access_token, // Bearer Supabase
        language,
        needs_onboarding,
        subscription_status,
        i18n,
        navigate,
        onLogin,
        useCookie: false, // con OAuth usiamo Bearer
        updateUser,
        fetchUserData,
        fromOnboarding,
      });
    } catch (err) {
      console.error("OAuth finalize error:", err);
      const msg =
        (err?.response?.data?.detail && Array.isArray(err.response.data.detail)
          ? err.response.data.detail[0]?.msg
          : err?.response?.data?.detail) ||
        t("auth.oauthError") ||
        "Errore nel callback OAuth";
      toast.error(msg);
      navigate("/", { replace: true });
    } finally {
      setGoogleLoading?.(false);
      _finalizeOAuthRunning = false;
    }
  }, [setGoogleLoading, setMobileAppLink, t, i18n, navigate, onLogin, updateUser, fetchUserData]);

  useEffect(() => {
    finalizeOAuth();
  }, [finalizeOAuth]);
}


/* =========================================================================================
   Componente Login
   ========================================================================================= */

const Login = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { updateUser, fetchUserData } = useUser();

  // Loader del Google button
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  // Link to return to mobile app after OAuth (shown on web when user is on mobile browser)
  const [mobileAppLink, setMobileAppLink] = useState(null);

  // Error detail modal (replaces toast for debugging)
  const [errorModal, setErrorModal] = useState(null);

  // Progressive loading messages — different for onboarding vs normal login
  const isFromOnboarding = new URLSearchParams(location.search).get('from') === 'onboarding'
    || localStorage.getItem('bh_oauth_from_onboarding') === '1';

  const loadingMessages = isFromOnboarding
    ? (i18n.language === 'it'
        ? [
            'Stiamo creando il tuo account…',
            'Stiamo salvando il tuo piano nel profilo…',
            'Preparazione del tuo piano personalizzato…',
            'Quasi pronto…',
          ]
        : [
            'Creating your account…',
            'Saving your plan to your profile…',
            'Preparing your personalized plan…',
            'Almost ready…',
          ])
    : (i18n.language === 'it'
        ? [
            'Bentornato…',
            'Stiamo recuperando i tuoi progressi…',
            'Quasi pronto…',
          ]
        : [
            'Welcome back…',
            'Retrieving your progress…',
            'Almost ready…',
          ]);

  useEffect(() => {
    if (!googleLoading) { setLoadingStep(0); return; }
    const timer = setInterval(() => {
      setLoadingStep(prev => (prev < loadingMessages.length - 1 ? prev + 1 : prev));
    }, 3000);
    return () => clearInterval(timer);
  }, [googleLoading, loadingMessages.length]);

  // Safety net: if loading overlay is stuck for more than 60s, force-clear it.
  // NOTE: this timer starts when googleLoading becomes true (before the OAuth
  // browser opens), so the user's time on Google's login page counts against it.
  // 60s gives plenty of room for slow auth + PKCE exchange + backend upsert.
  useEffect(() => {
    if (!googleLoading) return;
    const safetyTimer = setTimeout(() => {
      console.warn('[Login] Safety timeout — clearing stuck loading overlay after 60s');
      setGoogleLoading(false);
      resetAllGuards();
      setErrorModal({
        title: 'Login Timed Out (60s)',
        details: [
          `Timestamp: ${new Date().toISOString()}`,
          `Platform: ${typeof Capacitor !== 'undefined' ? Capacitor.getPlatform() : 'web'}`,
          `Native: ${typeof Capacitor !== 'undefined' ? Capacitor.isNativePlatform() : false}`,
          `_oauthHandled: ${_oauthHandled}`,
          `_finalizeOAuthRunning: ${_finalizeOAuthRunning}`,
          `isFromOnboarding: ${isFromOnboarding}`,
          `loadingStep: ${loadingStep}`,
          'The 60s safety timer expired while googleLoading was true.',
          'This means the deep link callback never completed (or never arrived).'
        ],
      });
    }, 60000);
    return () => clearTimeout(safetyTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleLoading, i18n.language]);

  // Fully grown garden grid (same pattern as GetStarted/Plan)

  // Finalizzazione OAuth al rientro
  useGoogleAuthFinalize(onLogin, setGoogleLoading, updateUser, fetchUserData, setMobileAppLink);

  // Listen for deep link returns on mobile (OAuth callback)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    let mounted = true;
    
    const handleAppUrlOpen = async ({ url }) => {
      // Ignore non-auth deep links for this flow.
      if (!url.includes('login') && !url.includes('oauth') && !url.includes('access_token') && !url.includes('code=')) {
        console.log('[handleAppUrlOpen] IGNORED non-auth URL:', url?.substring(0, 100));
        return;
      }

      console.log('[handleAppUrlOpen] DEEP LINK RECEIVED:', url?.substring(0, 200));

      // Guard: only one handler should finalize OAuth
      if (_oauthHandled) {
        console.log('[handleAppUrlOpen] SKIPPED — already handled');
        return;
      }
      _oauthHandled = true;
      console.log('[handleAppUrlOpen] Processing OAuth deep link...');

      setGoogleLoading(true);

      // Close the in-app browser immediately so the user sees the app
      try { await Browser.close(); } catch (_) { /* browser may already be closed */ }

      try {
        let session = null;

        // Parse URL params — try query string first, then hash fragment
        let code = null;
        let accessToken = null;
        let refreshToken = null;

        // Try query params
        try {
          const urlObj = new URL(url);
          code = urlObj.searchParams.get('code');
          accessToken = urlObj.searchParams.get('access_token');
          refreshToken = urlObj.searchParams.get('refresh_token');
        } catch (_) {
          const qi = url.indexOf('?');
          if (qi !== -1) {
            const qp = new URLSearchParams(url.substring(qi + 1));
            code = qp.get('code');
            accessToken = qp.get('access_token');
            refreshToken = qp.get('refresh_token');
          }
        }

        // Hash fragment — Supabase implicit flow puts tokens here
        if (!accessToken && url.includes('#')) {
          try {
            const hashStr = url.split('#')[1];
            const hashParams = new URLSearchParams(hashStr);
            accessToken = hashParams.get('access_token');
            refreshToken = hashParams.get('refresh_token');
          } catch (_) {}
        }

        console.log('[handleAppUrlOpen] Parsed URL params:', { code: !!code, accessToken: !!accessToken, refreshToken: !!refreshToken });


        // ── PKCE flow: exchange authorization code for session ──
        if (code) {
          console.log('[handleAppUrlOpen] PKCE: exchanging code for session...');

          // The Supabase SDK's exchangeCodeForSession() Promise deadlocks on
          // Capacitor Android because the Preferences-based token persistence
          // never resolves.  However, the SDK *does* fire onAuthStateChange
          // with SIGNED_IN as soon as the HTTP exchange succeeds (~1-2 s).
          // Strategy: race the SDK call against a one-shot auth-state listener
          // so we capture whichever succeeds first.

          let authUnsub = null;
          const authSignedIn = new Promise((resolve) => {
            const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
              if (event === 'SIGNED_IN' && sess?.user?.id) {
                console.log('[handleAppUrlOpen] SIGNED_IN captured via listener — user_id:', sess.user.id);
                resolve(sess);
              }
            });
            authUnsub = () => { try { subscription.unsubscribe(); } catch (_) {} };
          });

          const exchangePromise = (async () => {
            try {
              const { data, error } = await supabase.auth.exchangeCodeForSession(code);
              if (!error && data?.session) {
                console.log('[handleAppUrlOpen] exchangeCodeForSession resolved — user_id:', data.session.user?.id);
                return data.session;
              }
              if (error) console.error('[handleAppUrlOpen] exchangeCode SDK error:', error.message);
            } catch (e) {
              console.warn('[handleAppUrlOpen] exchangeCode threw:', e.message);
            }
            return null;
          })();

          const timeoutPromise = new Promise((resolve) =>
            setTimeout(() => resolve(null), 20000)
          );

          try {
            session = await Promise.race([
              authSignedIn,
              exchangePromise.then(s => s || new Promise(() => {})),  // only resolve if non-null
              timeoutPromise,
            ]);

            if (session) {
              console.log('[handleAppUrlOpen] PKCE race won — user_id:', session.user?.id);
            } else {
              console.warn('[handleAppUrlOpen] PKCE race timed out (20 s) — no session');
            }
          } finally {
            if (authUnsub) authUnsub();
          }
        }

        // ── Implicit flow: set session from tokens ──
        if (!session && accessToken && refreshToken) {
          console.log('[handleAppUrlOpen] Implicit flow: setting session from tokens...');
          try {
            const { data, error } = await Promise.race([
              supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }),
              new Promise((_, reject) => setTimeout(() => reject(new Error('setSession timeout 3s')), 3000)),
            ]);
            if (!error && data?.session) {
              session = data.session;
              console.log('[handleAppUrlOpen] setSession SUCCESS — user_id:', session.user?.id);
            } else {
              console.warn('[handleAppUrlOpen] setSession failed:', error?.message || 'no session returned');
            }
          } catch (err) {
            // supabase.auth.setSession hangs on Capacitor due to async storage.
            // Decode the JWT to extract user info and build a session manually.
            try {
              const payload = JSON.parse(atob(accessToken.split('.')[1]));
              session = {
                access_token: accessToken,
                refresh_token: refreshToken,
                user: {
                  id: payload.sub,
                  email: payload.email,
                  user_metadata: payload.user_metadata || {},
                  identities: [],
                },
              };

              // Fire-and-forget: try to persist the session in the background
              // so subsequent app opens have it available.
              supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).catch(() => {});
            } catch (decodeErr) {
            }
          }
        }

        // If we have access token but no refresh token, build manual session from JWT
        if (!session && accessToken && !refreshToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            session = {
              access_token: accessToken,
              refresh_token: null,
              user: {
                id: payload.sub,
                email: payload.email,
                user_metadata: payload.user_metadata || {},
                identities: [],
              },
            };
          } catch (_) {}
        }

        // ── Fallback: probe existing session ──
        if (!session) {
          console.log('[handleAppUrlOpen] No session yet — probing existing session...');
          try {
            const probe = await Promise.race([
              supabase.auth.getSession(),
              new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 3000)),
            ]);
            session = probe?.data?.session || null;
            if (session) console.log('[handleAppUrlOpen] Probe found session — user_id:', session.user?.id);
          } catch (_) {}
        }
        if (!session) {
          console.log('[handleAppUrlOpen] Still no session — waiting 1s + waitForSession(5s)...');
          await new Promise((r) => setTimeout(r, 1000));
          session = await waitForSession(5000);
          if (session) console.log('[handleAppUrlOpen] waitForSession found session — user_id:', session.user?.id);
        }


        // No auth data at all → bail
        if (!code && !accessToken && !refreshToken && !session) {
          console.warn('[handleAppUrlOpen] BAIL — no code, no tokens, no session');
          resetAllGuards();
          setGoogleLoading(false);
          return;
        }

        console.log('[handleAppUrlOpen] Session status before backend call:', {
          hasSession: !!session,
          userId: session?.user?.id || 'none',
          hasAccessToken: !!session?.access_token,
        });

        if (session?.user?.id && mounted) {
          // Upsert to backend and finalize login
          console.log('[handleAppUrlOpen] Calling upsertBackendUserFromGoogle...');
          const { language, needs_onboarding, subscription_status } = await Promise.race([
            upsertBackendUserFromGoogle({ session, API }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('upsert timeout 8s')), 8000)),
          ]);
          console.log('[handleAppUrlOpen] Backend upsert OK:', { language, needs_onboarding, subscription_status });
          const fromOnboarding = localStorage.getItem('bh_oauth_from_onboarding') === '1';
          await finalizeAndRoute({
            userId: session.user.id,
            accessToken: session.access_token,
            language,
            needs_onboarding,
            subscription_status,
            i18n,
            navigate,
            onLogin,
            useCookie: false,
            updateUser,
            fetchUserData,
            fromOnboarding,
          });

        } else {
          // Last resort: try JWT decode if we have an access token
          if (accessToken) {
            try {
              const payload = JSON.parse(atob(accessToken.split('.')[1]));
              session = {
                access_token: accessToken,
                refresh_token: refreshToken,
                user: { id: payload.sub, email: payload.email, user_metadata: payload.user_metadata || {}, identities: [] },
              };
              // Retry upsert with this manual session
              const { language, needs_onboarding, subscription_status } = await Promise.race([
                upsertBackendUserFromGoogle({ session, API }),
                new Promise((_, reject) => setTimeout(() => reject(new Error('upsert timeout 8s')), 8000)),
              ]);
              const fromOnboarding = localStorage.getItem('bh_oauth_from_onboarding') === '1';
              await finalizeAndRoute({
                userId: session.user.id, accessToken: session.access_token, language, needs_onboarding,
                subscription_status, i18n, navigate, onLogin, useCookie: false, updateUser, fetchUserData, fromOnboarding,
              });
              return;
            } catch (_lastErr) {
              console.error('Last resort JWT decode also failed', _lastErr);
            }
          }
          console.log('No session found after deep link', { accessTokenPresent: !!accessToken, refreshTokenPresent: !!refreshToken });
          setErrorModal({
            title: 'No Session After Deep Link',
            details: [
              `Timestamp: ${new Date().toISOString()}`,
              `Platform: ${typeof Capacitor !== 'undefined' ? Capacitor.getPlatform() : 'web'}`,
              `URL (first 200): ${url?.substring(0, 200)}`,
              `code present: ${!!code}`,
              `accessToken present: ${!!accessToken}`,
              `refreshToken present: ${!!refreshToken}`,
              `_oauthHandled: ${_oauthHandled}`,
              `_finalizeOAuthRunning: ${_finalizeOAuthRunning}`,
              `_cachedCodeVerifier: ${!!_cachedCodeVerifier}`,
              'All strategies (PKCE, implicit, setSession, JWT decode) failed to produce a session.'
            ],
          });
          resetAllGuards(); // allow retry
          setGoogleLoading(false);
        }
      } catch (error) {
        console.error('Error handling deep link:', error);
        setErrorModal({
          title: 'Deep Link Error (catch)',
          details: [
            `Timestamp: ${new Date().toISOString()}`,
            `Platform: ${typeof Capacitor !== 'undefined' ? Capacitor.getPlatform() : 'web'}`,
            `Error: ${error?.message || String(error)}`,
            `Stack: ${error?.stack || 'N/A'}`,
            `URL (first 200): ${url?.substring(0, 200)}`,
            `code present: ${!!code}`,
            `accessToken present: ${!!accessToken}`,
            `refreshToken present: ${!!refreshToken}`,
            `_oauthHandled: ${_oauthHandled}`,
            `_finalizeOAuthRunning: ${_finalizeOAuthRunning}`,
          ],
        });
        resetAllGuards(); // allow retry
        setGoogleLoading(false);
      }
    };
    
    // Register the listener and keep explicit handles for cleanup.
    let appUrlHandle = null;
    App.addListener('appUrlOpen', handleAppUrlOpen)
      .then((handle) => { appUrlHandle = handle; })
      .catch((err) => console.warn('[appUrlOpen] listener registration failed', err));

    // Cold-start OAuth callback: if app opened directly from deep link, process it.
    App.getLaunchUrl()
      .then((launch) => {
        if (launch?.url) {
          console.log('[OAuth] Processing launch URL:', launch.url.substring(0, 200));
          handleAppUrlOpen({ url: launch.url });
        }
      })
      .catch((err) => console.warn('[OAuth] getLaunchUrl failed', err));

    // ── FALLBACK: appStateChange ──
    // On Android, browserFinished doesn't fire when Chrome Custom Tab closes.
    // When the app resumes to foreground during an OAuth flow, check if the
    // deep link handler already ran. If not, probe for a session.
    let stateHandle = null;
    App.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive || !mounted) return;
      // Only act if OAuth was in progress but no handler finalized it
      if (_oauthHandled) return;
      // Small delay to let appUrlOpen fire first if it's going to
      await new Promise(r => setTimeout(r, 2000));
      // Re-check after delay — appUrlOpen may have handled it during the wait
      if (_oauthHandled || !mounted) return;

      console.log('[appStateChange] App resumed during OAuth — probing for session...');

      // Retry session probe multiple times — the PKCE exchange may still be
      // running in the background (e.g. Supabase detects the URL hash/params
      // or a previous exchangeCodeForSession call completes late).
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 3000;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        if (_oauthHandled || !mounted) return;
        try {
          const probe = await Promise.race([
            supabase.auth.getSession(),
            new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 3000)),
          ]);
          console.log(`[appStateChange] Probe attempt ${attempt}/${MAX_RETRIES}:`, {
            hasSession: !!probe?.data?.session,
            userId: probe?.data?.session?.user?.id || 'none',
          });
          if (probe?.data?.session?.user?.id && mounted && !_oauthHandled) {
            // Lock BEFORE any async work to prevent appUrlOpen from also proceeding
            _oauthHandled = true;
            console.log('[appStateChange] Session found, finalizing login...');
            const session = probe.data.session;
            try {
              const { language, needs_onboarding, subscription_status } = await Promise.race([
                upsertBackendUserFromGoogle({ session, API }),
                new Promise((_, reject) => setTimeout(() => reject(new Error('upsert timeout 8s')), 8000)),
              ]);
              const fromOnboarding = localStorage.getItem('bh_oauth_from_onboarding') === '1';
              await finalizeAndRoute({
                userId: session.user.id,
                accessToken: session.access_token,
                language, needs_onboarding, subscription_status,
                i18n, navigate, onLogin,
                useCookie: false, updateUser, fetchUserData, fromOnboarding,
              });
            } catch (err) {
              console.error('[appStateChange] Finalize failed:', err);
              resetAllGuards();
              setGoogleLoading(false);
            }
            return; // done
          }
        } catch (_) {}
        // Wait before next retry (unless last attempt)
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, RETRY_DELAY));
        }
      }
      // All retries exhausted — no session found
      if (mounted && !_oauthHandled) {
        console.log('[appStateChange] No session after all retries — clearing spinner');
        setGoogleLoading(false);
      }
    })
      .then((handle) => { stateHandle = handle; })
      .catch((err) => console.warn('[appStateChange] listener registration failed', err));
    
    return () => {
      mounted = false;
      try { appUrlHandle?.remove?.(); } catch (_) {}
      try { stateHandle?.remove?.(); } catch (_) {}
    };
  }, [i18n, navigate, onLogin, updateUser, fetchUserData]);

  // Stato form Email/Password
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    localStorage.removeItem('betterhusbandExplicitLogout');

    try {
      if (isLogin) {
        // LOGIN EMAIL/PASSWORD
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });

        if (response.data?.success) {
          const { user_id, needs_onboarding, language, token, subscription_status } = response.data;
          console.log('Login successful:', { user_id, token, needs_onboarding, subscription_status });

          // Se backend usa JWT, imposta Authorization di default
          if (token) {
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            console.log('Set Authorization header for JWT token');
          } else {
            // Per login email/password senza JWT, usa X-User-Id header
            axios.defaults.headers.common['X-User-Id'] = user_id;
            console.log('Set X-User-Id header:', user_id);
          }

          console.log('Axios defaults after login:', axios.defaults.headers.common);

          // Se usi cookie di sessione, abilita withCredentials per tutte le richieste
          // (rimosso perché ora usiamo X-User-Id)

          const fromOnboarding = new URLSearchParams(location.search).get('from') === 'onboarding';
          await finalizeAndRoute({
            userId: user_id,
            accessToken: token,          // undefined se usi X-User-Id
            language,
            needs_onboarding,
            subscription_status,
            i18n,
            navigate,
            onLogin,
            useCookie: false,            // Non usare cookie, ora usiamo X-User-Id
            updateUser,
            fromOnboarding,
          });
        }
      } else {
        // REGISTRAZIONE
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          language: i18n.language || "en",
        });

        if (response.data?.success) {
          const { user_id, needs_onboarding, subscription_status } = response.data;
          localStorage.setItem("betterhusbandUserId", user_id);
          localStorage.setItem("betterhusbandUserName", formData.name);
          localStorage.setItem("betterhusbandUserEmail", formData.email);

          // Set auth header for subsequent requests
          axios.defaults.headers.common['X-User-Id'] = user_id;

          const fromOnboarding = new URLSearchParams(location.search).get('from') === 'onboarding';
          await finalizeAndRoute({
            userId: user_id,
            accessToken: undefined,
            language: i18n.language || "en",
            needs_onboarding: needs_onboarding !== false,
            subscription_status: subscription_status || 'free',
            i18n,
            navigate,
            onLogin,
            useCookie: false,
            updateUser,
            fromOnboarding,
          });
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      
      // Determina il messaggio di errore appropriato
      let errorMsg = "Si è verificato un errore imprevisto";
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.status === 401) {
        errorMsg = isLogin 
          ? (t("auth.loginError") || "Email o password errati") 
          : (t("auth.registerError") || "Errore durante la registrazione");
      } else if (error.response?.status === 409) {
        errorMsg = "Sessione già attiva su un altro dispositivo. Verrà chiusa automaticamente.";
        // Force logout other sessions
        try {
            const authHeader = axios.defaults.headers.common.Authorization;
            const xUserId = axios.defaults.headers.common['X-User-Id'];
            const headers = authHeader ? { Authorization: authHeader } : (xUserId ? { 'X-User-Id': xUserId } : {});

            await axios.post(`${API}/auth/logout-others`, {}, { headers });
            setTimeout(() => handleSubmit(e), 1000);
            return;
          } catch (logoutError) {
            errorMsg = "Errore nel chiudere altre sessioni. Riprova.";
          }
      } else if (error.response?.status >= 500) {
        // backend internal server error
        console.error("Backend 5xx error:", error.response);
        errorMsg = isLogin 
          ? "Errore del server. Riprova tra qualche istante." 
          : "Errore del server durante la registrazione. Riprova più tardi.";
      } else {
        errorMsg = isLogin 
          ? (t("auth.loginError") || "Email o password errati") 
          : (t("auth.registerError") || "Errore durante la registrazione");
      }
      
      // Mostra sempre il toast di errore
      toast.error(errorMsg, {
        duration: 5000, // Mostra per 5 secondi
        position: "top-center"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleGoogleLogin = async () => {
    await handleOAuthLogin('google');
  };

  const handleAppleLogin = async () => {
    await handleOAuthLogin('apple');
  };

  const handleOAuthLogin = async (provider) => {
    try {
      setGoogleLoading(true); // mostra loader subito al click
      localStorage.removeItem('betterhusbandExplicitLogout');

      const isNativeMobile = Capacitor.isNativePlatform();
      console.log(`[OAuth] Starting ${provider} login — isNativeMobile=${isNativeMobile}, platform=${Capacitor.getPlatform()}`);
      
      // Determine redirect target based on platform
      // Native mobile should use app deep link so subscription tokens return to app
      const fromOnboarding = new URLSearchParams(location.search).get('from') === 'onboarding';
      if (fromOnboarding) {
        localStorage.setItem('bh_oauth_from_onboarding', '1');
      } else {
        localStorage.removeItem('bh_oauth_from_onboarding');
      }

      // NATIVE: Redirect directly to the app's custom scheme.
      // Supabase does a server-side HTTP 302 → betterhusband://login?code=XXX.
      // This works reliably in both SFSafariViewController (iOS) and Chrome
      // Custom Tabs (Android) because it's a server-side redirect, NOT a
      // JavaScript navigation (which iOS blocks for custom schemes).
      // The code_verifier is already stored in Capacitor Preferences by
      // signInWithOAuth below, so exchangeCodeForSession will find it.
      // WEB: Route through /auth/callback so AuthCallback exchanges the
      // PKCE code, then navigates to /login?oauth=success with session ready.
      const redirectUrl = isNativeMobile
        ? 'betterhusband://login'
        : `https://betterhusband.space/auth/callback${fromOnboarding ? '?from=onboarding' : ''}`;

      console.log(`[OAuth] redirectUrl=${redirectUrl}`);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: isNativeMobile, // Don't auto-redirect on native
          queryParams: {
            prompt: provider === 'google' ? 'select_account' : 'login',
          },
        },
      });

      if (error) {
        console.error(`[OAuth] signInWithOAuth error:`, error.message || error);
        toast.error(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login failed`);
        setGoogleLoading(false);
        return;
      }

      // Cache the PKCE code_verifier immediately — Supabase JS just stored it.
      // We need this for the manual HTTP fallback if exchangeCodeForSession hangs.
      if (isNativeMobile) {
        try {
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
          let projectRef = '';
          try {
            projectRef = new URL(supabaseUrl).hostname.split('.')[0] || '';
          } catch (_) {}

          const candidateKeys = [
            projectRef ? `sb-${projectRef}-auth-token-code-verifier` : null,
            projectRef ? `sb-${projectRef}-code-verifier` : null,
            'supabase.auth.code_verifier',
          ].filter(Boolean);

          let cv = null;
          for (const key of candidateKeys) {
            cv = await capacitorStorage.getItem(key);
            if (cv) {
              console.log('[OAuth] Found PKCE code_verifier key:', key);
              break;
            }
          }

          // Last resort: scan preference keys for any code-verifier entry.
          if (!cv) {
            try {
              const { keys } = await Preferences.keys();
              const dynamicKey = (keys || []).find((k) => k && k.includes('code-verifier'));
              if (dynamicKey) {
                cv = await capacitorStorage.getItem(dynamicKey);
                if (cv) {
                  console.log('[OAuth] Found PKCE code_verifier via scanned key:', dynamicKey);
                }
              }
            } catch (_) {}
          }

          if (cv) {
            _cachedCodeVerifier = cv;
            console.log('[OAuth] Cached PKCE code_verifier for fallback');
          } else {
            console.warn('[OAuth] code_verifier not found in storage after signInWithOAuth');
          }
        } catch (e) {
          console.warn('[OAuth] Failed to cache code_verifier:', e.message);
        }
      }

      console.log(`[OAuth] signInWithOAuth OK — url=${data?.url?.substring(0, 100)}`);

      if (data?.url) {
        if (isNativeMobile) {
          // On native mobile, open in-app browser
          console.log("[OAuth] Opening OAuth in Capacitor Browser:", data.url.substring(0, 100));
          
          // Reset the module-level guard so a fresh OAuth attempt can proceed
          _oauthHandled = false;
          
          // Listen for browser close. The deep-link handler (appUrlOpen) is the
          // primary path; this listener is a fallback that fires when the user
          // dismisses the browser. If the deep link already handled OAuth we
          // skip; otherwise we try to finalize or reset the spinner.
          let browserListener = null;
          const checkSessionAfterBrowser = async () => {
            console.log("Browser closed — checking for session...");
            // Remove this one-shot listener immediately
            try { browserListener?.remove?.(); } catch (_) {}
            
            // Give the deep link handler a moment to run first (3s for slow Android)
            await new Promise(r => setTimeout(r, 3000));
            
            // If deep link already handled everything, nothing to do
            if (_oauthHandled) {
              console.log("OAuth already handled by deep link, skipping browserFinished.");
              return;
            }
            
            // Probe for session — maybe the web redirect set it
            const probe = await Promise.race([
              supabase.auth.getSession(),
              new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 3000)),
            ]);
            if (probe?.data?.session?.user?.id) {
              // Guard before finalizing
              if (_oauthHandled) return;
              _oauthHandled = true;
              
              console.log("Session found after browser close, finalizing login...");
              try {
                const session = probe.data.session;
                const { language, needs_onboarding, subscription_status } =
                  await upsertBackendUserFromGoogle({ session, API });
                const fromOnboarding = localStorage.getItem('bh_oauth_from_onboarding') === '1';
                await finalizeAndRoute({
                  userId: session.user.id,
                  accessToken: session.access_token,
                  language,
                  needs_onboarding,
                  subscription_status,
                  i18n,
                  navigate,
                  onLogin,
                  useCookie: false,
                  updateUser,
                  fetchUserData,
                  fromOnboarding,
                });
              } catch (err) {
                console.error("Error finalizing after browser close:", err);
                resetAllGuards();
                setGoogleLoading(false);
              }
            } else {
              console.log("No session after browser close — user likely cancelled OAuth");
              setGoogleLoading(false);
            }
          };
          
          // Add one-shot listener for browser finish
          browserListener = await Browser.addListener('browserFinished', checkSessionAfterBrowser);
          
          // Open the OAuth URL in system browser (required for Google OAuth)
          await Browser.open({ 
            url: data.url,
            presentationStyle: 'popover',
            windowName: '_blank'
          });

        } else {
          // On web, do normal redirect
          window.location.href = data.url;
        }
      } else {
        setGoogleLoading(false);
      }
    } catch (err) {
      console.error(`Unexpected error during ${provider} login:`, err);
      setGoogleLoading(false);
      toast.error(`Login with ${provider.charAt(0).toUpperCase() + provider.slice(1)} failed. Please try again.`, {
        duration: 5000,
        position: "top-center"
      });
    }
  };

  // Email form expansion
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const lang = i18n.language?.startsWith('it') ? 'it' : i18n.language?.startsWith('es') ? 'es' : 'en';

  return (
    <div style={{
      height: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'hsl(var(--background))',
      color: '#000',
      position: 'relative', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      paddingTop: 0, paddingBottom: 0,
    }}>
      <LanguageSelector light />

      {/* ── Full-screen progressive loading overlay ── */}
      {googleLoading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: '#F5F0E8',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid rgba(0,0,0,0.12)',
            borderTopColor: '#111',
            animation: 'spin 0.8s linear infinite',
            marginBottom: 32,
          }} />
          <motion.p
            key={loadingStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            style={{
              fontFamily: 'Manrope-Variable, Manrope, sans-serif',
              fontSize: 17, fontWeight: 600, color: '#111',
              textAlign: 'center', maxWidth: 280,
            }}
          >
            {loadingMessages[loadingStep]}
          </motion.p>
          <div style={{
            marginTop: 24, width: 200, height: 4, borderRadius: 2,
            background: 'rgba(0,0,0,0.1)', overflow: 'hidden',
          }}>
            <motion.div
              initial={{ width: '5%' }}
              animate={{ width: `${Math.min(20 + loadingStep * 25, 95)}%` }}
              transition={{ duration: 2, ease: 'easeOut' }}
              style={{ height: '100%', borderRadius: 2, background: '#111' }}
            />
          </div>
        </div>
      )}

      {/* ── App icon + title ── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          position: 'relative', zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 10,
          paddingTop: 'max(calc(env(safe-area-inset-top, 16px) + 12px), 28px)',
          marginBottom: 24,
          color: '#000',
        }}
      >
        <img
          src={logoImg}
          alt="Better Husband"
          style={{
            width: 32, height: 32,
            borderRadius: 8,
          }}
        />
        <span style={{
          fontFamily: 'Manrope-Variable, Manrope, sans-serif',
          fontSize: 18, fontWeight: 700,
          color: '#000', letterSpacing: 0.3,
        }}>
          Better Husband
        </span>
      </motion.div>

      {/* ── Mobile App Return Banner ── */}
      {mobileAppLink && (
        <div style={{
          margin: '0 20px 12px', padding: 16,
          background: '#f0fdf4', borderRadius: 16, textAlign: 'center',
        }}>
          <div style={{ color: '#000', fontWeight: 600, marginBottom: 8, fontFamily: 'Manrope-Variable, Manrope, sans-serif' }}>
            Login completato!
          </div>
          <p style={{ color: '#000', fontSize: 13, marginBottom: 12, fontFamily: 'Manrope-Variable, Manrope, sans-serif' }}>
            Clicca il pulsante per tornare all'app
          </p>
          <a
            href={mobileAppLink}
            style={{
              display: 'block', padding: '12px 16px',
              background: '#E0D5C2', color: '#000',
              borderRadius: 12, fontWeight: 600, textDecoration: 'none',
              fontFamily: 'Manrope-Variable, Manrope, sans-serif',
            }}
          >
            Apri Better Husband
          </a>
        </div>
      )}

      {/* ── Bottom section: auth buttons ── */}
      <div style={{
        padding: '28px 24px calc(env(safe-area-inset-bottom, 24px) + 24px)',
        maxWidth: 440, width: '100%', margin: '0 auto',
        position: 'relative', zIndex: 2,
        flexShrink: 0,
        marginTop: 20,
      }}>

        {/* Contextual banner when arriving from onboarding */}
        {new URLSearchParams(location.search).get('from') === 'onboarding' && (
          <div style={{
            marginBottom: 16, padding: '14px 16px',
            background: '#DFF7E3', borderRadius: 14, textAlign: 'center',
            border: '1px solid #C5E9BF',
          }}>
            <p style={{
              fontFamily: 'Manrope-Variable, Manrope, sans-serif',
              fontSize: 14, fontWeight: 600, color: '#000', margin: 0, lineHeight: 1.4,
            }}>
              {i18n.language === 'it'
                ? 'Il tuo piano è pronto! Crea un account per salvarlo.'
                : 'Your plan is ready! Create an account to save it.'}
            </p>
          </div>
        )}

        {!showEmailForm ? (
          <>
            {/* Continue with Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              style={{
                width: '100%', height: 52,
                borderRadius: 12, border: '1.5px solid rgba(0,0,0,0.12)',
                background: '#E3DACD', cursor: 'pointer',
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontWeight: 600, fontSize: 15,
                color: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                marginBottom: 16,
                transition: 'background .2s',
              }}
            >
              {googleLoading ? (
                <div style={{ width: 20, height: 20, border: '2px solid #999', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>{i18n.language === 'it' ? 'Accedi con Google' : i18n.language === 'es' ? 'Inicia sesión con Google' : 'Sign in with Google'}</span>
                </>
              )}
            </button>

            {/* Continue with Apple — white outlined style per Apple HIG */}
            <button
              type="button"
              onClick={handleAppleLogin}
              disabled={googleLoading}
              style={{
                width: '100%', height: 52,
                borderRadius: 12, border: '1px solid #000',
                background: '#fff', cursor: 'pointer',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontWeight: 500, fontSize: 15,
                color: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 16,
                transition: 'background .2s',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#000" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>{i18n.language === 'it' ? 'Accedi con Apple' : i18n.language === 'es' ? 'Inicia sesión con Apple' : 'Sign in with Apple'}</span>
            </button>

            {/* Continue with email */}
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              style={{
                width: '100%', height: 52,
                borderRadius: 12, border: '1.5px solid rgba(0,0,0,0.12)',
                background: '#E3DACD', cursor: 'pointer',
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontWeight: 600, fontSize: 15,
                color: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                marginBottom: 20,
                transition: 'background .2s',
              }}
            >
              <Mail style={{ width: 18, height: 18, color: 'rgba(0,0,0,0.35)' }} />
              <span>{i18n.language === 'it' ? 'Continua con email' : i18n.language === 'es' ? 'Continuar con email' : 'Continue with email'}</span>
            </button>

            {/* Already have an account? */}
            <p style={{
              textAlign: 'center', fontSize: 13,
              color: 'rgba(0,0,0,0.75)',
              fontFamily: 'Manrope-Variable, Manrope, sans-serif',
              marginTop: 12,
            }}>
              {i18n.language === 'it' ? 'Hai già un account? ' : i18n.language === 'es' ? '¿Ya tienes una cuenta? ' : 'Already have an account? '}
              <button
                type="button"
                onClick={() => { setIsLogin(true); setShowEmailForm(true); }}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                  fontSize: 13, fontWeight: 700,
                  color: 'rgba(0,0,0,0.85)',
                  textDecoration: 'underline',
                }}
              >
                {i18n.language === 'it' ? 'Accedi' : i18n.language === 'es' ? 'Iniciar sesión' : 'Log in'}
              </button>
            </p>
          </>
        ) : (
          <>
            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 4, padding: 4,
              background: '#F0E6D6',
              borderRadius: 14, marginBottom: 16,
            }}>
              {[true, false].map((login) => (
                <button
                  key={login ? 'login' : 'register'}
                  onClick={() => setIsLogin(login)}
                  style={{
                    flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer',
                    borderRadius: 11,
                    background: isLogin === login ? '#000' : 'transparent',
                    boxShadow: isLogin === login ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                    fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                    fontWeight: 600, fontSize: 14,
                    color: isLogin === login ? '#fff' : 'rgba(0,0,0,0.45)',
                    transition: 'all .2s',
                  }}
                >
                  {login ? (t("auth.login") || "Accedi") : (t("auth.register") || "Registrati")}
                </button>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    placeholder={t("auth.namePlaceholder") || "Il tuo nome"}
                    style={{
                      width: '100%', padding: '12px 16px',
                      borderRadius: 12, border: '1.5px solid rgba(184,255,185,0.15)',
                      background: '#fff', outline: 'none',
                      fontFamily: 'Manrope-Variable, Manrope, sans-serif', fontSize: 16,
                      color: '#000', boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: 'rgba(0,0,0,0.35)' }} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t("auth.emailPlaceholder") || "tua@email.com"}
                    style={{
                      width: '100%', padding: '12px 16px 12px 42px',
                      borderRadius: 12, border: '1.5px solid rgba(184,255,185,0.15)',
                      background: '#fff', outline: 'none',
                      fontFamily: 'Manrope-Variable, Manrope, sans-serif', fontSize: 16,
                      color: '#000', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 6 }}>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: 'rgba(0,0,0,0.35)' }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    style={{
                      width: '100%', padding: '12px 44px 12px 42px',
                      borderRadius: 12, border: '1.5px solid rgba(184,255,185,0.15)',
                      background: '#fff', outline: 'none',
                      fontFamily: 'Manrope-Variable, Manrope, sans-serif', fontSize: 16,
                      color: '#000', boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(0,0,0,0.35)' }}
                  >
                    {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', height: 48, marginTop: 14,
                  borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: isLogin && formData.email && formData.password ? 'linear-gradient(135deg, #E36A55, #D35C48)' : '#E3DACD',
                  color: isLogin && formData.email && formData.password ? '#ffffff' : '#000',
                  boxShadow: isLogin && formData.email && formData.password ? '0 0 20px rgba(227, 106, 85, 0.25)' : '0 0 20px rgba(0, 0, 0, 0.08)',
                  fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                  fontWeight: 600, fontSize: 15,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: loading ? 0.55 : 1,
                  transition: 'opacity .2s',
                }}
              >
                {loading ? (
                  <div style={{ width: 20, height: 20, border: '2px solid #0d0f0b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                ) : (
                  isLogin ? (t("auth.loginButton") || "Accedi") : (t("auth.registerButton") || "Crea Account")
                )}
              </button>
            </form>

            {/* Back to main buttons */}
            <button
              type="button"
              onClick={() => setShowEmailForm(false)}
              style={{
                width: '100%', marginTop: 12,
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 13, fontWeight: 500,
                color: 'rgba(0,0,0,0.6)',
                textAlign: 'center', padding: '8px 0',
              }}
            >
              {i18n.language === 'it' ? '← Torna indietro' : i18n.language === 'es' ? '← Volver' : '← Go back'}
            </button>
          </>
        )}

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, lineHeight: 1.6, color: 'rgba(0,0,0,0.65)', fontFamily: 'Manrope-Variable, Manrope, sans-serif' }}>
          {i18n.language === 'it' ? 'Continuando, accetti la nostra ' : i18n.language === 'es' ? 'Al continuar, aceptas nuestra ' : 'By continuing, you agree to our '}
          <button
            type="button"
            onClick={() => setLegalModal('privacy')}
            style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'rgba(0,0,0,0.85)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'Manrope-Variable, Manrope, sans-serif' }}
          >
            {i18n.language === 'it' ? 'Privacy Policy' : i18n.language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
          </button>
          {i18n.language === 'it' ? ' e ' : i18n.language === 'es' ? ' y ' : ' and '}
          <button
            type="button"
            onClick={() => setLegalModal('terms')}
            style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'rgba(0,0,0,0.85)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'Manrope-Variable, Manrope, sans-serif' }}
          >
            {i18n.language === 'it' ? 'Termini di Servizio' : i18n.language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
          </button>
          .
        </div>
      </div>

      {/* Spin keyframe */}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <AnimatePresence>
        {legalModal && (
          <LegalModal
            type={legalModal}
            lang={lang}
            onClose={() => setLegalModal(null)}
          />
        )}
      </AnimatePresence>

      {/* Error detail modal for debugging OAuth failures */}
      {errorModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: '#1a1a2e', color: '#fff', borderRadius: 12,
            padding: 24, maxWidth: 420, width: '100%',
            maxHeight: '80vh', overflowY: 'auto',
            fontFamily: 'monospace', fontSize: 12,
          }}>
            <h3 style={{ margin: '0 0 12px', color: '#ff6b6b', fontSize: 16, fontFamily: 'sans-serif' }}>
              {errorModal.title}
            </h3>
            {errorModal.details.map((line, i) => (
              <div key={i} style={{
                padding: '4px 0',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                wordBreak: 'break-all',
              }}>
                {line}
              </div>
            ))}
            <button
              onClick={() => setErrorModal(null)}
              style={{
                marginTop: 16, width: '100%', padding: '12px',
                background: '#ff6b6b', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'sans-serif',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
